import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
}

const SITE_NAME = "Stream Solutions Limited";
const BASE_URL = "https://stream-solutions.com";
const DEFAULT_OG_IMAGE = "https://public.readdy.ai/ai/img_res/9051b913-a4fc-49c8-8d5f-b0cc0fd3b0da.png";

const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
}: SEOProps) => {
  useEffect(() => {
    // Title
    document.title = `${title} | ${SITE_NAME}`;

    // Helper to set or create meta tag
    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement("meta");
        const [attrName, attrVal] = selector.replace("meta[", "").replace("]", "").split('="');
        el.setAttribute(attrName, attrVal.replace('"', ""));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    const setMetaByName = (name: string, content: string) => {
      let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const setMetaByProp = (property: string, content: string) => {
      let el = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", property);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    // Basic meta
    setMetaByName("description", description);
    if (keywords) setMetaByName("keywords", keywords);
    setMetaByName("author", SITE_NAME);
    setMetaByName("robots", "index, follow");

    // Last modified
    setMetaByName("last-modified", new Date().toISOString().split("T")[0]);

    // Canonical
    const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : `${BASE_URL}${window.location.pathname}`;
    setLink("canonical", canonicalUrl);

    // Open Graph
    setMetaByProp("og:type", "website");
    setMetaByProp("og:site_name", SITE_NAME);
    setMetaByProp("og:title", ogTitle || `${title} | ${SITE_NAME}`);
    setMetaByProp("og:description", ogDescription || description);
    setMetaByProp("og:image", ogImage || DEFAULT_OG_IMAGE);
    setMetaByProp("og:url", canonicalUrl);
    setMetaByProp("og:locale", "ja_JP");

    // Twitter Card
    setMetaByName("twitter:card", "summary_large_image");
    setMetaByName("twitter:title", ogTitle || `${title} | ${SITE_NAME}`);
    setMetaByName("twitter:description", ogDescription || description);
    setMetaByName("twitter:image", ogImage || DEFAULT_OG_IMAGE);

    // Geo tags (Hong Kong HQ)
    setMetaByName("geo.region", "HK-KLN");
    setMetaByName("geo.placename", "Yau Tong, Kowloon, Hong Kong");
    setMetaByName("geo.position", "22.2950;114.2350");
    setMetaByName("ICBM", "22.2950, 114.2350");

    // Language
    document.documentElement.setAttribute("lang", "ja");
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage]);
};

export default useSEO;
